/**
 * @module stores/menu
 * @description 메뉴 트리 Pinia 스토어 — 백엔드에서 조회한 사용자 권한별 메뉴 트리를
 * 단일 소스로 관리합니다. HeaderMenu(PC), MobileDrawer(모바일), AppHeader(브레드크럼)가
 * 공유하여 getMenuList API 호출 중복을 제거하고, 탭 전환 시 사이드바 active/자동 펼침
 * 동기화의 근거 데이터로 사용됩니다.
 *
 * SURINP-218: ADMIN 전용 "리포트/통계" 메뉴를 menuStore.tree 에 정적 머지합니다.
 * 백엔드 메뉴 마스터 등록 없이 FE 단독으로 노출하기 위한 우회 패턴이며,
 * 향후 backend 가 동일 menuWebCode 를 등록하면 중복 가드로 보호됩니다.
 */
import { defineStore } from 'pinia'
import store from '@/store'
import { getMenuList } from '@/api/domain/common'
import { useTabStore } from '@/stores/tabs'

// 여러 컴포넌트가 동시에 loadMenu()를 호출할 때 getMenuList API가 중복 호출되는 것을 방지합니다.
let _loadingPromise = null
// 진행 중인 요청이 어느 계정 것인지·몇 번째 요청인지 — 계정 전환 중 이전 계정 요청을 재사용·반영하지 않는다 (SURINP-692).
let _loadingFor = ''
let _loadSeq = 0

// SURINP-218: ADMIN 전용 정적 메뉴 정의
const REPORT_MENU_GROUP = Object.freeze({
  menuWebCode: 'REPORT',
  name: '리포트/통계',
  isExpanded: false,
  childMenuList: [
    {
      menuWebCode: 'REPORT_MAIN',
      name: '리포트/통계',
      urlPath: '/report'
    }
  ]
})

// agent-admin dev-guide §3: ADMIN 전용 "AI 에이전트 관리" 정적 메뉴 (SURINP-218 패턴 재사용)
// 그룹 레벨 urlPath: 사이드바가 하위메뉴 없이 상위 메뉴 클릭만으로 직행하는 그룹임을 표시
// (BE 메뉴 마스터의 '대시보드' 그룹과 동일 규칙 — HeaderMenu 가 urlPath 유무로 판정).
// childMenuList 는 사이드바 active/브레드크럼의 urlPath 완전 일치 매칭용으로 유지한다.
const AGENT_ADMIN_MENU_GROUP = Object.freeze({
  menuWebCode: 'AGENT_ADMIN',
  name: 'AI 에이전트 관리',
  urlPath: '/agent-admin',
  isExpanded: false,
  childMenuList: [
    {
      menuWebCode: 'AGENT_ADMIN_MAIN',
      name: 'AI 에이전트 관리',
      urlPath: '/agent-admin'
    }
  ]
})

// SURINP-342: ADMIN 전용 정적 메뉴 정의 — 고객사·협력사 신청(customer-back 소유 화면).
// cs-back 메뉴 마스터에 없는 화면이므로 REPORT 와 같은 정적 머지 경로를 쓴다.
const PARTNERSHIP_MENU_GROUP = Object.freeze({
  menuWebCode: 'PARTNERSHIP_INQUIRY',
  name: '고객사·협력사 신청',
  isExpanded: false,
  childMenuList: [
    {
      menuWebCode: 'PARTNERSHIP_INQUIRY_LIST',
      name: '신청 관리',
      urlPath: '/partnership-inquiries'
    }
  ]
})

// mergeAdminMenus 가 순회하는 ADMIN 정적 메뉴 그룹 목록 — 신규 ADMIN 화면은 여기에만 추가
// (SURINP-342 파트너십 그룹은 main 의 mergePartnershipMenu() 경로를 그대로 유지 — 머지 시 통합 결정)
const ADMIN_MENU_GROUPS = [REPORT_MENU_GROUP, AGENT_ADMIN_MENU_GROUP]

// 파라미터 상세 라우트 → 소속 메뉴(목록) urlPath 매핑.
// 사이드바 active/자동 펼침·브레드크럼은 menu child.urlPath "완전 일치"로 판정하는데,
// 상세 화면(AI 위젯 카드/rowNav 이동 포함)은 파라미터가 붙은 별개 path 라 매칭이 불가능했다.
// path 네이밍이 도메인마다 달라(-detail 접미 / :id 세그먼트 / 별개 base) prefix 일괄 매칭 대신 명시 규칙을 둔다.
// SURINP-692: 메뉴 권한 가드도 이 매핑을 쓴다 — 무파라미터 상세·완료 상세까지 소속 목록 메뉴로 판정.
const DETAIL_MENU_RULES = [
  { test: /^\/settlement-management\/./, menuPath: '/settlement-management' },
  { test: /^\/consultation\/./, menuPath: '/consultation-history' },
  { test: /^\/repair-request-management-detail(\/|$)/, menuPath: '/repair-request-management' },
  { test: /^\/repair-completed-detail(\/|$)/, menuPath: '/repair-request-management' },
  { test: /^\/repair-request-history-detail(\/|$)/, menuPath: '/repair-request-history' }
]

// SURINP-692: 메뉴 트리와 무관하게 열리는 경로 — 로그인 직후 착지·차단 시 복귀 화면(대시보드 메뉴 없음 ≠ 접근 금지).
const MENU_EXEMPT_PATHS = ['/', '/dashboard']

/**
 * fullPath 를 사이드바 메뉴 매칭용 urlPath 로 정규화합니다.
 * 쿼리스트링·끝 슬래시를 제거하고, 상세 라우트면 소속 목록 메뉴의 urlPath 로 치환합니다.
 * (끝 슬래시: Vue Router 는 `/code-management/` 도 같은 라우트로 매칭한다 — SURINP-692)
 * @param {string} fullPath - 활성 탭의 fullPath
 * @returns {string} 메뉴 트리 child.urlPath 와 비교 가능한 경로
 */
export function resolveMenuPath(fullPath) {
  const path = normalizeMenuPath(fullPath)
  const rule = DETAIL_MENU_RULES.find((r) => r.test.test(path))
  return rule ? rule.menuPath : path
}

/**
 * fullPath 에서 쿼리스트링·끝 슬래시를 뗍니다 (resolveMenuPath·findMenuNameByPath 공용).
 * @param {string} fullPath
 * @returns {string}
 */
function normalizeMenuPath(fullPath) {
  const raw = (fullPath || '').split('?')[0]
  return raw.length > 1 ? raw.replace(/\/+$/, '') || '/' : raw
}

/**
 * 현재 로그인 계정 ID — 메뉴 트리를 어느 계정 것으로 불러왔는지 대조하는 데 씁니다 (SURINP-692).
 * @returns {string}
 */
function currentLoginId() {
  return store?.state?.auth?.user?.id?.loginId || ''
}

export const useMenuStore = defineStore('menu', {
  state: () => ({
    /** API 응답 메뉴 트리 (각 항목에 isExpanded 필드 포함) */
    tree: [],
    /** 로드 완료 플래그 — loadMenu의 idempotent 가드에 사용 */
    loaded: false,
    /** 트리를 불러온 계정 ID — 같은 탭에서 다른 계정으로 로그인하면 이전 트리를 쓰지 않는다 (SURINP-692) */
    loadedFor: ''
  }),
  getters: {
    /**
     * fullPath 로 소속 그룹(상위 메뉴)을 반환합니다.
     * urlPath 매칭 시 쿼리스트링은 제거하고 pathname 만 비교하며,
     * 파라미터 상세 라우트는 resolveMenuPath 로 소속 목록 메뉴에 매핑합니다.
     * @returns {(fullPath: string) => object|undefined}
     */
    findGroupByPath: (state) => (fullPath) => {
      const path = resolveMenuPath(fullPath)
      return state.tree.find((g) =>
        (g.childMenuList || []).some((c) => c.urlPath === path)
      )
    },
    /**
     * 현재 사용자가 fullPath 화면에 들어갈 메뉴 권한이 있는지 판정합니다 (SURINP-692).
     * 라우터 menuGuard 와 헤더 빠른 접수/상담 버튼이 같은 판정을 씁니다.
     * 제외 경로·ADMIN 은 허용, 메뉴 미로드(조회 실패 포함)·다른 계정의 트리는 거부(fail-closed),
     * 그 밖에는 그룹 urlPath(직행 그룹) 또는 child urlPath 가 일치해야 허용합니다.
     * @returns {(fullPath: string) => boolean}
     */
    /**
     * fullPath 에 해당하는 메뉴 마스터 이름을 반환합니다 — 화면 제목(탭 제목)용 (SURINP-688).
     * 경로가 child.urlPath 또는 직행 그룹 urlPath 와 정확히 같을 때만 이름을 주고,
     * 상세 라우트(DETAIL_MENU_RULES)·메뉴에 없는 경로는 빈 문자열(라우트 제목 유지)입니다. 권한 판정은 하지 않습니다.
     * @returns {(fullPath: string) => string}
     */
    findMenuNameByPath: (state) => (fullPath) => {
      const path = normalizeMenuPath(fullPath)
      if (DETAIL_MENU_RULES.some((r) => r.test.test(path))) return ''
      for (const g of state.tree) {
        const child = (g.childMenuList || []).find((c) => c.urlPath === path)
        if (child) return child.name || ''
      }
      const group = state.tree.find((g) => g.urlPath === path)
      return group ? group.name || '' : ''
    },
    canAccessPath: (state) => (fullPath) => {
      const path = resolveMenuPath(fullPath)
      if (MENU_EXEMPT_PATHS.includes(path)) return true
      if (store?.state?.auth?.user?.id?.userType === 'ADMIN') return true
      if (!state.loaded || state.loadedFor !== currentLoginId()) return false
      return state.tree.some((g) =>
        g.urlPath === path || (g.childMenuList || []).some((c) => c.urlPath === path)
      )
    }
  },
  actions: {
    /**
     * 메뉴 트리를 API 로 조회합니다. 같은 계정으로 이미 로드된 경우 재호출하지 않습니다(idempotent).
     * 다른 계정의 트리가 남아 있으면 비우고 다시 조회합니다 (SURINP-692).
     * 같은 계정의 동시 호출은 진행 중인 Promise를 공유하여 API 중복 호출을 방지하고,
     * 다른 계정의 요청이 진행 중이면 새로 조회하며 늦게 끝난 이전 계정 응답은 버립니다.
     * 에러 발생 시 조용히 실패하고 tree 는 빈 배열로 유지됩니다.
     * 로드 성공 후 ADMIN 사용자에게는 정적 리포트 메뉴를 머지합니다.
     * @returns {Promise<void>}
     */
    async loadMenu() {
      const loginId = currentLoginId()
      if (this.loaded && this.loadedFor === loginId) return
      if (_loadingPromise && _loadingFor === loginId) return _loadingPromise
      if (this.loadedFor !== loginId) {
        this.tree = []
        this.loaded = false
      }
      const seq = ++_loadSeq
      _loadingFor = loginId
      _loadingPromise = (async () => {
        try {
          const items = await getMenuList()
          if (seq !== _loadSeq) return
          this.tree = (items || []).map((g) => ({ ...g, isExpanded: false }))
          this.mergeAdminMenus()
          this.loaded = true
          this.loadedFor = loginId
          // 메뉴 트리보다 먼저 열린 탭(주소 직접 진입·새로고침 복원)의 제목을 메뉴명으로 맞춘다 (SURINP-688)
          useTabStore().applyMenuTitles((p) => this.findMenuNameByPath(p))
        } catch (e) {
          window.__APP_LOG__(e)
        } finally {
          if (seq === _loadSeq) _loadingPromise = null
        }
      })()
      return _loadingPromise
    },
    /**
     * ADMIN 사용자 한정으로 정적 ADMIN 그룹(리포트/통계, AI 에이전트 관리)을 tree 에 머지합니다.
     * 동일 menuWebCode 가 이미 존재하면 push 하지 않습니다(backend 가 향후 등록 시 보호).
     * userType 가 ADMIN 이 아니면 아무 동작도 하지 않습니다.
     */
    mergeAdminMenus() {
      const userType = store?.state?.auth?.user?.id?.userType
      if (userType !== 'ADMIN') return
      // SURINP-342: 고객사·협력사 신청 정적 머지 — 아래 그룹 loop 는 그룹별 개별 스킵이라
      // (main 원 주석의 early-return 문제 없음) 순서 영향은 없으나 기존 호출 관례를 유지한다.
      this.mergePartnershipMenu()
      ADMIN_MENU_GROUPS.forEach((group) => {
        // backend 메뉴 마스터에 동일 그룹이 등록되면(동일 menuWebCode 또는 동일 url_path 보유)
        // 정적 머지를 스킵한다. urlPath 매칭은 backend 가 'ko-report' 같은 다른 코드를 사용해도 보호.
        const groupPaths = group.childMenuList.map((c) => c.urlPath)
        const exists = this.tree.some((g) =>
          g.menuWebCode === group.menuWebCode ||
          (g.childMenuList || []).some((c) => groupPaths.includes(c.urlPath))
        )
        if (exists) return
        // childMenuList 까지 명시적으로 복사하여 외부 변경으로부터 정적 정의를 보호한다.
        this.tree = [
          ...this.tree,
          {
            ...group,
            childMenuList: group.childMenuList.map((c) => ({ ...c }))
          }
        ]
      })
    },
    /**
     * ADMIN 사용자 한정으로 정적 고객사·협력사 신청 그룹을 tree 에 머지합니다(SURINP-342).
     * mergeAdminMenus() 에서만 호출되므로 등급 검사는 그쪽에서 이미 끝나 있습니다.
     * 동일 menuWebCode 또는 동일 urlPath 가 이미 존재하면 push 하지 않습니다.
     */
    mergePartnershipMenu() {
      const exists = this.tree.some((g) =>
        g.menuWebCode === PARTNERSHIP_MENU_GROUP.menuWebCode ||
        (g.childMenuList || []).some((c) => c.urlPath === '/partnership-inquiries')
      )
      if (exists) return
      this.tree = [
        ...this.tree,
        {
          ...PARTNERSHIP_MENU_GROUP,
          childMenuList: PARTNERSHIP_MENU_GROUP.childMenuList.map((c) => ({ ...c }))
        }
      ]
    },
    /**
     * 특정 경로에 해당하는 그룹의 isExpanded를 true로 설정합니다.
     * 다른 그룹의 isExpanded 상태는 보존됩니다(탭 전환 시 누적 펼침).
     * @param {string} fullPath - 활성 탭의 fullPath
     */
    setExpandedByPath(fullPath) {
      const group = this.findGroupByPath(fullPath)
      if (!group) return
      this.tree = this.tree.map((g) => ({
        ...g,
        isExpanded: g.menuWebCode === group.menuWebCode ? true : g.isExpanded
      }))
    },
    /**
     * 상위 메뉴 그룹의 isExpanded 를 토글합니다.
     * HeaderMenu / MobileDrawer 가 직접 호출하여 아코디언 동작에 사용합니다.
     * @param {string|number} menuWebCode - 토글할 그룹의 식별자
     */
    toggleGroup(menuWebCode) {
      this.tree = this.tree.map((g) =>
        g.menuWebCode === menuWebCode ? { ...g, isExpanded: !g.isExpanded } : g
      )
    },
    /**
     * 모든 메뉴 상태를 초기화합니다. 로그아웃 시 호출합니다.
     */
    clearAll() {
      this.tree = []
      this.loaded = false
      this.loadedFor = ''
      _loadingPromise = null
      _loadingFor = ''
      _loadSeq++
    }
  }
})
